import Axios from "helpers/Interceptor";
import { OrganizationRole, UserPermissionOnBrand } from "modules/Const";
import { CurrentBrand, OrganizationData, OrganizationInvitationStatus, OrganizationStatus, OrganizationTeamData } from "modules/Types";

export const getOrganization_api = async ({
    organizationId,
    brandId,
    userId,
}: {
    organizationId?: number;
    brandId?: number;
    userId?: number;
}): Promise<OrganizationTeamData | null | undefined> => {
    try {
        const queryParams = new URLSearchParams();
        if (brandId) {
            queryParams.append('brandId', brandId.toString());
        }
        if (userId) {
            queryParams.append('userId', userId.toString());
        }

        const queryString = queryParams.toString();
        const url = `/v2/organization/${organizationId}${queryString ? `?${queryString}` : ''}`;

        const res = await Axios.get(url);
        return res.data;
    } catch (error) {
        return null;
    }
}

export const createOrganization_api = async ({
    name,
    emails,
    setupStatus,
    ownerId,
}: {
    name: string;
    emails: string[];
    setupStatus: OrganizationStatus;
    ownerId: number;
}): Promise<OrganizationTeamData | null | undefined> => {
    try {
        const res = await Axios.post('/v2/organization', {
            name,
            emails,
            setupStatus,
            ownerId,
        });
        return res.data;
    } catch (error) {
        return null;
    }
}

export const updateOrganization_api = async ({
    organizationId,
    name,
    setupStatus,
}: {
    organizationId: number;
    name: string;
    setupStatus: OrganizationStatus;
}): Promise<{ organization: any } | null | undefined> => {
    try {
        const res = await Axios.put(`/v2/organization/${organizationId}`, {
            name,
            setupStatus,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to update organization:', error);
        return null;
    }
}

export const inviteOrganizationUser_api = async ({
    organizationId,
    users,
    brands,
}: {
    organizationId: number;
    users: {
        email: string;
        role: OrganizationRole;
        firstName: string;
        lastName: string;
    }[];
    brands: CurrentBrand[];
}): Promise<OrganizationTeamData | null | undefined> => {
    try {
        const res = await Axios.post('/v2/invite-organization-users', {
            organizationId,
            users,
            brands,
        });
        return res.data;
    } catch (error) {
        return null;
    }
}

export const respondOrganizationUserInvite_api = async ({
    organizationId,
    userId,
    invitationStatus,
    brandIds,
}: {
    organizationId: number;
    userId: number;
    invitationStatus: OrganizationInvitationStatus;
    brandIds?: number[];
}): Promise<OrganizationTeamData | null | undefined> => {
    try {
        const res = await Axios.post(`/v2/respond-organization-user-invite`, {
            organizationId,
            userId,
            invitationStatus,
            brandIds,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to respond to organization user invite:', error);
        return null;
    }
}

export const checkOrganizationUserInvitationStatus_api = async ({
    organizationId,
    userId,
}: {
    organizationId: number;
    userId: number;
}): Promise<{ invitationStatus: OrganizationInvitationStatus } | null | undefined> => {
    try {
        const res = await Axios.post(`/v2/check-organization-user-invitation-status`, {
            organizationId,
            userId,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to check organization user invitation status:', error);
        return null;
    }
}

export const createOrganizationBrand_api = async ({
    organizationId,
    brandId,
}: {
    organizationId: number;
    brandId: number;
}): Promise<{ organizationBrands: any[], organizationUsers: any[] } | null | undefined> => {
    try {
        const res = await Axios.post(`/v2/organization-brand/${organizationId}`, {
            brandId,
        });
        return res.data
    } catch (error) {
        console.error('Failed to create organization brand:', error);
        return null;
    }
}

export const updateOrganizationUser_api = async ({
    organizationId,
    userId,
    role,
    brands,
}: {
    organizationId: number;
    userId: number;
    role: OrganizationRole;
    brands: any[];
}): Promise<{ organizationUsers: any[] }> => {
    try {
        const res = await Axios.put(`/v2/organization-user/${organizationId}/${userId}`, {
            role,
            brands,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to update organization user:', error);
        return { organizationUsers: [] };
    }
}

export const updateUserBrand_api = async ({
    organizationId,
    brandId,
    users,
}: {
    organizationId: number;
    brandId: number;
    users: {
        userId: number;
        permissionLevel: UserPermissionOnBrand;
    }[];
}): Promise<{ organizationUsers: any[] }> => {
    try {
        const res = await Axios.put(`/v2/organization-user-brand/${organizationId}/${brandId}`, {
            users,
        });
        return res.data;
    } catch (error) {
        console.error('Failed to update user brand:', error);
        return { organizationUsers: [] };
    }
}

export const deleteOrganizationBrand_api = async ({
    organizationId,
    brandId,
}: {
    organizationId: number;
    brandId: number;
}): Promise<{ organizationUsers: any[] } | null | undefined> => {
    try {
        const res = await Axios.delete(`/v2/organization-brand/${organizationId}/${brandId}`);
        return res.data;
    } catch (error) {
        console.error('Failed to remove organization brand:', error);
        return null;
    }
}

export const deleteOrganizationUser_api = async ({
    organizationId,
    userId,
}: {
    organizationId: number;
    userId: number;
}): Promise<{ organizationBrands: any[], removedBrands: any[] } | null | undefined> => {
    try {
        const res = await Axios.delete(`/v2/organization-user/${organizationId}/${userId}`);
        return res.data;
    } catch (error) {
        console.error('Failed to remove organization user:', error);
        return null;
    }
}